import type { PlayEvent, PlayEventParticipant } from '../types/playEvent';

type ParticipantNameFields = Pick<
  PlayEventParticipant,
  | 'guestFirstName'
  | 'guestLastName'
  | 'displayFirstName'
  | 'displayLastName'
  | 'participantType'
>;

type ParticipantAvatarFields = Pick<
  PlayEventParticipant,
  | 'avatarUrl'
  | 'guestFirstName'
  | 'guestLastName'
  | 'displayFirstName'
  | 'displayLastName'
>;

/** Name parts for UserAvatar (guests use displayName; members/students use first/last). */
export function getPlayEventParticipantAvatarProps(participant: ParticipantAvatarFields): {
  avatarUrl?: string | null;
  firstName?: string;
  lastName?: string;
  displayName?: string;
} {
  if (participant.guestFirstName || participant.guestLastName) {
    return {
      avatarUrl: participant.avatarUrl,
      displayName: [participant.guestFirstName, participant.guestLastName].filter(Boolean).join(' ').trim(),
    };
  }

  return {
    avatarUrl: participant.avatarUrl,
    firstName: participant.displayFirstName ?? undefined,
    lastName: participant.displayLastName ?? undefined,
  };
}

export function getPlayEventParticipantDisplayName(participant: ParticipantNameFields): string {
  if (participant.guestFirstName || participant.guestLastName) {
    return [participant.guestFirstName, participant.guestLastName].filter(Boolean).join(' ').trim();
  }

  if (participant.displayFirstName || participant.displayLastName) {
    return [participant.displayFirstName, participant.displayLastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  if (participant.participantType === 'member') return 'Member';
  if (participant.participantType === 'student') return 'Student';
  return 'Guest';
}

export function playEventAsksSinglesPreferenceAtCheckIn(
  event: Pick<PlayEvent, 'format' | 'socialPlayMode' | 'oddPlayerPolicy'>
): boolean {
  return (
    event.format === 'social_rotate' &&
    event.socialPlayMode === 'prefer_doubles' &&
    event.oddPlayerPolicy === 'fallback_singles'
  );
}

/** Operator-only label for a participant's singles/doubles preference at check-in. */
export function getPlayEventParticipantSinglesPreferenceLabel(
  event: Pick<PlayEvent, 'format' | 'socialPlayMode' | 'oddPlayerPolicy'>,
  prefersSingles?: boolean | null
): string | null {
  if (!playEventAsksSinglesPreferenceAtCheckIn(event)) {
    return null;
  }
  return prefersSingles ? 'Prefers singles' : 'Prefers doubles';
}

export function isPlayEventParticipantRosterSelectable(
  participant: Pick<PlayEventParticipant, 'status'>
): boolean {
  return participant.status === 'registered' || participant.status === 'checked_in';
}

export function canCheckInPlayEventParticipant(
  event: Pick<PlayEvent, 'status'>,
  participant: Pick<PlayEventParticipant, 'status'>
): boolean {
  return (
    participant.status === 'registered' &&
    (event.status === 'published' ||
      event.status === 'check_in_open' ||
      event.status === 'in_progress')
  );
}

export function canRemovePlayEventParticipantFromRoster(
  event: Pick<PlayEvent, 'status'>,
  participant: Pick<PlayEventParticipant, 'status'>
): boolean {
  return event.status === 'draft' && isPlayEventParticipantRosterSelectable(participant);
}

export function canWithdrawPlayEventParticipantFromRoster(
  event: Pick<PlayEvent, 'status'>,
  participant: Pick<PlayEventParticipant, 'status'>
): boolean {
  return (
    event.status !== 'draft' &&
    event.status !== 'cancelled' &&
    event.status !== 'completed' &&
    isPlayEventParticipantRosterSelectable(participant)
  );
}

/** Participant fields safe to expose on public browse, boards, and self-service join. */
export type PublicPlayEventParticipant = Pick<
  PlayEventParticipant,
  | 'id'
  | 'playEventId'
  | 'participantType'
  | 'memberId'
  | 'studentId'
  | 'guestFirstName'
  | 'guestLastName'
  | 'displayFirstName'
  | 'displayLastName'
  | 'avatarUrl'
  | 'gender'
  | 'skillLevel'
  | 'status'
  | 'checkedInAt'
>;

/** Participant fields used on public boards and matchup cards. */
export type PlayEventDisplayParticipant = PublicPlayEventParticipant | PlayEventParticipant;

export function toPublicPlayEventParticipant(
  participant: PlayEventParticipant
): PublicPlayEventParticipant {
  return {
    id: participant.id,
    playEventId: participant.playEventId,
    participantType: participant.participantType,
    memberId: participant.memberId,
    studentId: participant.studentId,
    guestFirstName: participant.guestFirstName,
    guestLastName: participant.guestLastName,
    displayFirstName: participant.displayFirstName,
    displayLastName: participant.displayLastName,
    avatarUrl: participant.avatarUrl,
    gender: participant.gender,
    skillLevel: participant.skillLevel,
    status: participant.status,
    checkedInAt: participant.checkedInAt,
  };
}

/** Event statuses that allow player-facing check-in (kiosk tablet or authenticated self check-in). */
export function isPlayEventPlayerCheckInOpen(event: Pick<PlayEvent, 'status'>): boolean {
  return event.status === 'check_in_open' || event.status === 'in_progress';
}
